/**
 * Terrain Decorator - adds visual decorations like trees, rocks, snow to terrain
 */

import * as THREE from 'three';
import { getTerrainType, tileHeight, isLand, getSurfaceY } from './TerrainGenerator';
import { hash2, fbm } from '@/core/utils/noise';
import { obstacleRegistry } from '@/game/systems/ObstacleRegistry';
import { clearPathfindingCache } from '@/core/utils/Pathfinding';

const MAX_TREES = 6000;       // 森林
const MAX_ROCKS = 1500;       // 山丘
const MAX_SNOW = 1200;        // 雪原
const MAX_DESERT = 1200;      // 荒漠
const MAX_VOLCANO = 1200;     // 火山
const MAX_GRASSLAND = 2000;   // 草原
const MAX_SWAMP = 1200;       // 沼泽

// ============================================
// 多层噪声群落系统 - 确保所有区域都有覆盖
// ============================================

// 群落内密度（块内要密集）
const TREE_DENSITY = 0.85;
const ROCK_DENSITY = 0.85;
const SNOW_DENSITY = 0.8;
const DESERT_DENSITY = 0.75;
const VOLCANO_DENSITY = 0.8;
const GRASS_DENSITY = 0.9;
const SWAMP_DENSITY = 0.8;

/**
 * 使用多层噪声判断是否在群落内
 * 高阈值 = 更稀疏的分块
 */
function isInPatch(wx: number, wz: number, _seed: number): boolean {
  // 第一层：大尺度噪声 - 产生少量大群落
  const largeScale = 0.06;
  const largeNoise = fbm(wx * largeScale, wz * largeScale);
  if (largeNoise > 0.7) return true;  // 只有峰值区域

  // 第二层：中尺度噪声 - 产生中等群落块
  const medScale = 0.12;
  const medNoise = fbm(wx * medScale + 100, wz * medScale + 100);
  if (medNoise > 0.72) return true;

  // 第三层：小尺度噪声 - 产生小群落块
  const smallScale = 0.25;
  const smallNoise = fbm(wx * smallScale + 200, wz * smallScale + 200);

  // 小群落只在部分格子出现，增加随机性
  const cellX = Math.floor(wx / 10);
  const cellZ = Math.floor(wz / 10);
  const cellRand = hash2(cellX * 7919, cellZ * 7927);

  if (smallNoise > 0.68 && cellRand < 0.4) return true;

  // 极少量散点（2%）- 保证不会完全空白
  const scatterHash = hash2(
    Math.floor(wx * 1.5) * 31337,
    Math.floor(wz * 1.5) * 31357
  );
  if (scatterHash < 0.02) return true;

  return false;
}

interface DecoInstance {
  x: number;
  y: number;
  z: number;
  scale: number;
  rotation: number;
}

export class TerrainDecorator {
  private scene: THREE.Scene;
  private treeMesh!: THREE.InstancedMesh;      // 森林
  private rockMesh!: THREE.InstancedMesh;      // 山丘
  private snowMesh!: THREE.InstancedMesh;      // 雪原
  private desertMesh!: THREE.InstancedMesh;    // 荒漠
  private volcanoMesh!: THREE.InstancedMesh;   // 火山
  private grassMesh!: THREE.InstancedMesh;     // 草原
  private swampMesh!: THREE.InstancedMesh;     // 沼泽

  private treeInstances: DecoInstance[] = [];
  private rockInstances: DecoInstance[] = [];
  private snowInstances: DecoInstance[] = [];
  private desertInstances: DecoInstance[] = [];
  private volcanoInstances: DecoInstance[] = [];
  private grassInstances: DecoInstance[] = [];
  private swampInstances: DecoInstance[] = [];

  private processedChunks: Set<string> = new Set();
  private dummy = new THREE.Object3D();

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    this.createMeshes();
  }

  private createMeshes(): void {
    // Trees for forest (林国)
    this.treeMesh = new THREE.InstancedMesh(
      this.createTreeGeometry(),
      new THREE.MeshLambertMaterial({ vertexColors: true }),
      MAX_TREES
    );
    this.treeMesh.count = 0;
    this.treeMesh.castShadow = true;
    this.treeMesh.receiveShadow = true;
    this.scene.add(this.treeMesh);

    // Rocks for highland (铁国)
    this.rockMesh = new THREE.InstancedMesh(
      this.createRockGeometry(),
      new THREE.MeshLambertMaterial({ vertexColors: true }),
      MAX_ROCKS
    );
    this.rockMesh.count = 0;
    this.rockMesh.castShadow = true;
    this.rockMesh.receiveShadow = true;
    this.scene.add(this.rockMesh);

    // Snow/ice crystals for snow terrain (寒国)
    this.snowMesh = new THREE.InstancedMesh(
      this.createSnowGeometry(),
      new THREE.MeshLambertMaterial({ vertexColors: true }),
      MAX_SNOW
    );
    this.snowMesh.count = 0;
    this.snowMesh.castShadow = true;
    this.scene.add(this.snowMesh);

    // Cacti for desert (云国)
    this.desertMesh = new THREE.InstancedMesh(
      this.createCactusGeometry(),
      new THREE.MeshLambertMaterial({ vertexColors: true }),
      MAX_DESERT
    );
    this.desertMesh.count = 0;
    this.desertMesh.castShadow = true;
    this.scene.add(this.desertMesh);

    // Volcanic rocks for volcano (炎国)
    this.volcanoMesh = new THREE.InstancedMesh(
      this.createVolcanoGeometry(),
      new THREE.MeshLambertMaterial({ vertexColors: true }),
      MAX_VOLCANO
    );
    this.volcanoMesh.count = 0;
    this.volcanoMesh.castShadow = true;
    this.scene.add(this.volcanoMesh);

    // Grass tufts for grassland (商国)
    this.grassMesh = new THREE.InstancedMesh(
      this.createGrassGeometry(),
      new THREE.MeshLambertMaterial({ vertexColors: true, side: THREE.DoubleSide }),
      MAX_GRASSLAND
    );
    this.grassMesh.count = 0;
    this.scene.add(this.grassMesh);

    // Swamp plants for swamp (玄国)
    this.swampMesh = new THREE.InstancedMesh(
      this.createSwampGeometry(),
      new THREE.MeshLambertMaterial({ vertexColors: true }),
      MAX_SWAMP
    );
    this.swampMesh.count = 0;
    this.swampMesh.castShadow = true;
    this.scene.add(this.swampMesh);
  }

  private createTreeGeometry(): THREE.BufferGeometry {
    // Multi-layered low-poly tree like reference image
    const trunkGeo = new THREE.CylinderGeometry(0.12, 0.18, 0.6, 6);
    trunkGeo.translate(0, 0.3, 0);

    // Multiple cone layers for fuller tree
    const leaves1 = new THREE.ConeGeometry(0.55, 0.7, 7);
    leaves1.translate(0, 0.9, 0);
    const leaves2 = new THREE.ConeGeometry(0.45, 0.6, 7);
    leaves2.translate(0, 1.3, 0);
    const leaves3 = new THREE.ConeGeometry(0.3, 0.5, 6);
    leaves3.translate(0, 1.65, 0);

    return this.mergeGeometries(
      [trunkGeo, leaves1, leaves2, leaves3],
      [
        new THREE.Color(0x5d4037),  // Brown trunk
        new THREE.Color(0x2e7d32),  // Dark green
        new THREE.Color(0x388e3c),  // Medium green
        new THREE.Color(0x43a047),  // Light green
      ]
    );
  }

  private createRockGeometry(): THREE.BufferGeometry {
    // Large rock formation with multiple rocks clustered
    const rock1 = new THREE.DodecahedronGeometry(0.4, 0);
    rock1.scale(1.2, 0.7, 1);
    rock1.translate(0, 0.25, 0);

    const rock2 = new THREE.IcosahedronGeometry(0.25, 0);
    rock2.scale(1, 0.6, 0.8);
    rock2.translate(0.35, 0.15, 0.1);

    const rock3 = new THREE.OctahedronGeometry(0.2, 0);
    rock3.scale(1, 0.5, 1);
    rock3.translate(-0.25, 0.1, 0.2);

    return this.mergeGeometries(
      [rock1, rock2, rock3],
      [
        new THREE.Color(0x616161),  // Dark gray
        new THREE.Color(0x757575),  // Medium gray
        new THREE.Color(0x9e9e9e),  // Light gray
      ]
    );
  }

  private createSnowGeometry(): THREE.BufferGeometry {
    // Snow-covered rock / ice formation
    const base = new THREE.DodecahedronGeometry(0.35, 0);
    base.scale(1, 0.5, 1);
    base.translate(0, 0.2, 0);

    const snowCap = new THREE.IcosahedronGeometry(0.3, 0);
    snowCap.scale(1.1, 0.3, 1.1);
    snowCap.translate(0, 0.4, 0);

    const icicle = new THREE.ConeGeometry(0.08, 0.25, 4);
    icicle.translate(0.15, 0.15, 0.1);

    return this.mergeGeometries(
      [base, snowCap, icicle],
      [
        new THREE.Color(0x90a4ae),  // Blue-gray rock
        new THREE.Color(0xeceff1),  // White snow
        new THREE.Color(0xb3e5fc),  // Light blue ice
      ]
    );
  }

  private createCactusGeometry(): THREE.BufferGeometry {
    // Yellow sand base
    const sandBase = new THREE.CylinderGeometry(0.5, 0.55, 0.12, 8);
    sandBase.translate(0, 0.06, 0);

    // Desert cactus with arms
    const bodyGeo = new THREE.CylinderGeometry(0.12, 0.15, 0.8, 8);
    bodyGeo.translate(0, 0.5, 0);

    const arm1 = new THREE.CylinderGeometry(0.07, 0.08, 0.35, 6);
    arm1.rotateZ(-Math.PI / 3);
    arm1.translate(0.2, 0.6, 0);

    const arm2 = new THREE.CylinderGeometry(0.06, 0.07, 0.3, 6);
    arm2.rotateZ(Math.PI / 2.5);
    arm2.translate(-0.18, 0.7, 0);

    // Small desert rock
    const rock = new THREE.OctahedronGeometry(0.15, 0);
    rock.scale(1.2, 0.4, 1);
    rock.translate(0.3, 0.12, 0.15);

    return this.mergeGeometries(
      [sandBase, bodyGeo, arm1, arm2, rock],
      [
        new THREE.Color(0xd4a84b),  // Yellow sand
        new THREE.Color(0x558b2f),  // Dark cactus green
        new THREE.Color(0x689f38),  // Medium green
        new THREE.Color(0x7cb342),  // Light green
        new THREE.Color(0xbcaaa4),  // Sandy rock
      ]
    );
  }

  private createVolcanoGeometry(): THREE.BufferGeometry {
    // 火山岩/熔岩石 (炎国)
    const lavaRock = new THREE.DodecahedronGeometry(0.35, 0);
    lavaRock.scale(1.3, 0.6, 1.1);
    lavaRock.translate(0, 0.2, 0);

    // 熔岩裂缝效果
    const crack1 = new THREE.BoxGeometry(0.08, 0.15, 0.5);
    crack1.translate(0, 0.25, 0);

    // 小火山岩
    const smallRock = new THREE.IcosahedronGeometry(0.2, 0);
    smallRock.scale(1, 0.5, 1);
    smallRock.translate(0.3, 0.12, 0.2);

    // 烟雾/蒸汽效果（用小球表示）
    const steam = new THREE.SphereGeometry(0.12, 6, 4);
    steam.translate(0, 0.5, 0);

    return this.mergeGeometries(
      [lavaRock, crack1, smallRock, steam],
      [
        new THREE.Color(0x3d2817),  // 深棕色火山岩
        new THREE.Color(0xd84315),  // 橙红色熔岩
        new THREE.Color(0x4a3728),  // 深色岩石
        new THREE.Color(0x808080),  // 灰色烟雾
      ]
    );
  }

  private createGrassGeometry(): THREE.BufferGeometry {
    // 草原草丛 (商国)
    // 多个草叶片
    const blade1 = new THREE.PlaneGeometry(0.08, 0.5);
    blade1.translate(0, 0.25, 0);
    blade1.rotateY(Math.PI / 6);

    const blade2 = new THREE.PlaneGeometry(0.07, 0.45);
    blade2.translate(0.1, 0.22, 0.05);
    blade2.rotateY(-Math.PI / 4);

    const blade3 = new THREE.PlaneGeometry(0.08, 0.55);
    blade3.translate(-0.08, 0.27, 0.08);
    blade3.rotateY(Math.PI / 3);

    const blade4 = new THREE.PlaneGeometry(0.06, 0.4);
    blade4.translate(0.05, 0.2, -0.1);
    blade4.rotateY(-Math.PI / 5);

    // 小花朵
    const flower = new THREE.SphereGeometry(0.06, 6, 4);
    flower.translate(0.12, 0.35, 0);

    return this.mergeGeometries(
      [blade1, blade2, blade3, blade4, flower],
      [
        new THREE.Color(0x7cb342),  // 亮绿
        new THREE.Color(0x8bc34a),  // 草绿
        new THREE.Color(0x689f38),  // 深绿
        new THREE.Color(0x9ccc65),  // 浅绿
        new THREE.Color(0xffd54f),  // 黄色花朵
      ]
    );
  }

  private createSwampGeometry(): THREE.BufferGeometry {
    // 沼泽植物 (玄国)
    // 沼泽水面基座
    const waterBase = new THREE.CylinderGeometry(0.4, 0.45, 0.08, 8);
    waterBase.translate(0, 0.04, 0);

    // 芦苇/沼泽草
    const reed1 = new THREE.CylinderGeometry(0.03, 0.04, 0.7, 5);
    reed1.translate(0, 0.4, 0);

    const reed2 = new THREE.CylinderGeometry(0.025, 0.035, 0.6, 5);
    reed2.translate(0.15, 0.35, 0.1);
    reed2.rotateZ(0.1);

    const reed3 = new THREE.CylinderGeometry(0.03, 0.04, 0.65, 5);
    reed3.translate(-0.1, 0.37, 0.12);
    reed3.rotateZ(-0.08);

    // 蘑菇（神秘感）
    const mushroom = new THREE.SphereGeometry(0.1, 6, 4);
    mushroom.scale(1, 0.5, 1);
    mushroom.translate(0.25, 0.1, -0.1);

    return this.mergeGeometries(
      [waterBase, reed1, reed2, reed3, mushroom],
      [
        new THREE.Color(0x3e4a3e),  // 深沼泽绿
        new THREE.Color(0x4a5d4a),  // 芦苇深绿
        new THREE.Color(0x556b55),  // 芦苇中绿
        new THREE.Color(0x4e614e),  // 芦苇
        new THREE.Color(0x6a1b9a),  // 紫色蘑菇（玄国色）
      ]
    );
  }

  private mergeGeometries(geometries: THREE.BufferGeometry[], colors: THREE.Color[]): THREE.BufferGeometry {
    // Convert indexed geometries to non-indexed for proper merging
    const nonIndexedGeos: THREE.BufferGeometry[] = [];
    for (const geo of geometries) {
      nonIndexedGeos.push(geo.index ? geo.toNonIndexed() : geo);
    }

    let totalPositions = 0;
    for (const geo of nonIndexedGeos) {
      totalPositions += geo.attributes.position.array.length;
    }

    const positions = new Float32Array(totalPositions);
    const vertexColors = new Float32Array(totalPositions);

    let offset = 0;
    for (let i = 0; i < nonIndexedGeos.length; i++) {
      const geo = nonIndexedGeos[i];
      const posArray = geo.attributes.position.array as Float32Array;
      const color = colors[i];

      positions.set(posArray, offset);

      for (let j = 0; j < posArray.length; j += 3) {
        const variation = 0.9 + Math.random() * 0.2;
        vertexColors[offset + j] = color.r * variation;
        vertexColors[offset + j + 1] = color.g * variation;
        vertexColors[offset + j + 2] = color.b * variation;
      }

      offset += posArray.length;
    }

    // Dispose original geometries
    for (const geo of geometries) {
      geo.dispose();
    }
    for (const geo of nonIndexedGeos) {
      if (!geometries.includes(geo)) {
        geo.dispose();
      }
    }

    const merged = new THREE.BufferGeometry();
    merged.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    merged.setAttribute('color', new THREE.BufferAttribute(vertexColors, 3));
    merged.computeVertexNormals();

    return merged;
  }

  /**
   * Process a chunk and add decorations based on terrain type
   */
  processChunk(chunkX: number, chunkZ: number, chunkSize: number, tileSize: number): void {
    const key = `${chunkX},${chunkZ}`;
    if (this.processedChunks.has(key)) return;
    this.processedChunks.add(key);

    const startX = chunkX * chunkSize;
    const startZ = chunkZ * chunkSize;

    for (let z = 0; z < chunkSize; z++) {
      for (let x = 0; x < chunkSize; x++) {
        const gx = startX + x;
        const gz = startZ + z;
        const wx = (gx + 0.5) * tileSize;
        const wz = (gz + 0.5) * tileSize;

        const h = tileHeight(wx, wz);
        if (!isLand(h)) continue;

        const terrainType = getTerrainType(wx, wz, h);
        const decoHash = hash2(gx * 12345 + 789, gz * 54321 + 123);

        // 检查是否在装饰物群落内（统一群落系统）
        const inPatch = isInPatch(wx, wz, 0);

        // 不在群落内，跳过
        if (!inPatch) continue;

        // Scale larger than tile size, with some variation
        const scale = 1.0 + hash2(gx * 555, gz * 666) * 0.4;
        const rotation = hash2(gx * 777, gz * 888) * Math.PI * 2;

        // Place at tile center, on top of tile surface
        const instance: DecoInstance = {
          x: wx,
          y: getSurfaceY(wx, wz) + 0.5,
          z: wz,
          scale,
          rotation,
        };

        // 根据地形类型生成对应的装饰物
        let isObstacle = false;

        if (terrainType === 'forest' &&
            decoHash < TREE_DENSITY &&
            this.treeInstances.length < MAX_TREES) {
          this.treeInstances.push(instance);
          isObstacle = true;
        } else if (terrainType === 'highland' &&
            decoHash < ROCK_DENSITY &&
            this.rockInstances.length < MAX_ROCKS) {
          this.rockInstances.push(instance);
          isObstacle = true;
        } else if (terrainType === 'snow' &&
            decoHash < SNOW_DENSITY &&
            this.snowInstances.length < MAX_SNOW) {
          this.snowInstances.push(instance);
          isObstacle = true;
        } else if (terrainType === 'desert' &&
            decoHash < DESERT_DENSITY &&
            this.desertInstances.length < MAX_DESERT) {
          this.desertInstances.push(instance);
          isObstacle = true;
        } else if (terrainType === 'volcano' &&
            decoHash < VOLCANO_DENSITY &&
            this.volcanoInstances.length < MAX_VOLCANO) {
          this.volcanoInstances.push(instance);
          isObstacle = true;
        } else if (terrainType === 'grassland' &&
            decoHash < GRASS_DENSITY &&
            this.grassInstances.length < MAX_GRASSLAND) {
          this.grassInstances.push(instance);
          // 草丛不是障碍物，可以穿过
        } else if (terrainType === 'swamp' &&
            decoHash < SWAMP_DENSITY &&
            this.swampInstances.length < MAX_SWAMP) {
          this.swampInstances.push(instance);
          isObstacle = true;
        }

        // 注册障碍物到碰撞系统
        if (isObstacle) {
          obstacleRegistry.registerObstacle(wx, wz);
        }
      }
    }

    // Clear pathfinding cache since new obstacles were added
    clearPathfindingCache();

    this.updateAllMeshes();
  }

  /**
   * Remove decorations from a chunk
   */
  removeChunk(chunkX: number, chunkZ: number, chunkSize: number, tileSize: number): void {
    const key = `${chunkX},${chunkZ}`;
    if (!this.processedChunks.has(key)) return;
    this.processedChunks.delete(key);

    // Calculate bounds
    const minX = chunkX * chunkSize * tileSize;
    const maxX = (chunkX + 1) * chunkSize * tileSize;
    const minZ = chunkZ * chunkSize * tileSize;
    const maxZ = (chunkZ + 1) * chunkSize * tileSize;

    const inBounds = (d: DecoInstance) => d.x >= minX && d.x < maxX && d.z >= minZ && d.z < maxZ;

    // Helper to remove obstacles from registry
    const removeObstacles = (instances: DecoInstance[]) => {
      for (const d of instances) {
        if (inBounds(d)) {
          obstacleRegistry.removeObstacle(d.x, d.z);
        }
      }
    };

    // Remove obstacles from registry (except grass which is not an obstacle)
    removeObstacles(this.treeInstances);
    removeObstacles(this.rockInstances);
    removeObstacles(this.snowInstances);
    removeObstacles(this.desertInstances);
    removeObstacles(this.volcanoInstances);
    removeObstacles(this.swampInstances);

    // Remove all decorations in this chunk
    this.treeInstances = this.treeInstances.filter((d) => !inBounds(d));
    this.rockInstances = this.rockInstances.filter((d) => !inBounds(d));
    this.snowInstances = this.snowInstances.filter((d) => !inBounds(d));
    this.desertInstances = this.desertInstances.filter((d) => !inBounds(d));
    this.volcanoInstances = this.volcanoInstances.filter((d) => !inBounds(d));
    this.grassInstances = this.grassInstances.filter((d) => !inBounds(d));
    this.swampInstances = this.swampInstances.filter((d) => !inBounds(d));

    // Clear pathfinding cache since obstacles changed
    clearPathfindingCache();

    this.updateAllMeshes();
  }

  private updateAllMeshes(): void {
    this.updateInstancedMesh(this.treeMesh, this.treeInstances);
    this.updateInstancedMesh(this.rockMesh, this.rockInstances);
    this.updateInstancedMesh(this.snowMesh, this.snowInstances);
    this.updateInstancedMesh(this.desertMesh, this.desertInstances);
    this.updateInstancedMesh(this.volcanoMesh, this.volcanoInstances);
    this.updateInstancedMesh(this.grassMesh, this.grassInstances);
    this.updateInstancedMesh(this.swampMesh, this.swampInstances);
  }

  private updateInstancedMesh(mesh: THREE.InstancedMesh, instances: DecoInstance[]): void {
    mesh.count = instances.length;

    for (let i = 0; i < instances.length; i++) {
      const inst = instances[i];
      this.dummy.position.set(inst.x, inst.y, inst.z);
      this.dummy.rotation.y = inst.rotation;
      this.dummy.scale.setScalar(inst.scale);
      this.dummy.updateMatrix();
      mesh.setMatrixAt(i, this.dummy.matrix);
    }

    mesh.instanceMatrix.needsUpdate = true;
  }

  /**
   * Get decoration counts
   */
  getTreeCount(): number {
    return this.treeInstances.length;
  }

  getDecorationCounts(): {
    trees: number;
    rocks: number;
    snow: number;
    desert: number;
    volcano: number;
    grass: number;
    swamp: number;
  } {
    return {
      trees: this.treeInstances.length,
      rocks: this.rockInstances.length,
      snow: this.snowInstances.length,
      desert: this.desertInstances.length,
      volcano: this.volcanoInstances.length,
      grass: this.grassInstances.length,
      swamp: this.swampInstances.length,
    };
  }

  dispose(): void {
    const meshes = [
      this.treeMesh,
      this.rockMesh,
      this.snowMesh,
      this.desertMesh,
      this.volcanoMesh,
      this.grassMesh,
      this.swampMesh,
    ];
    for (const mesh of meshes) {
      this.scene.remove(mesh);
      mesh.geometry.dispose();
      (mesh.material as THREE.Material).dispose();
    }

    this.treeInstances = [];
    this.rockInstances = [];
    this.snowInstances = [];
    this.desertInstances = [];
    this.volcanoInstances = [];
    this.grassInstances = [];
    this.swampInstances = [];
    this.processedChunks.clear();
  }
}
